import React from 'react';
import PropTypes from 'prop-types';
import { sentenceCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
import isCartPastLimit from '../../helpers/cart/isCartPastLimit';
import determineLimit from '../../helpers/cart/determineLimit';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

const GiftCardContainer = (props) => {
	const { addItemToCart, generateSubtotal, giftCard, siteId, subtotal, userBalance } = props;

	const navigate = useNavigate();

	const [quantity, setQuantity] = React.useState(1);
	const [hasBeenAdded, setHasBeenAdded] = React.useState(false);
	const [confirmAddition, setConfirmAddition] = React.useState(false);

	React.useEffect(() => {
		if (hasBeenAdded) {
			setTimeout(() => {
				setHasBeenAdded(false);
			}, 500);
		}

		if (confirmAddition) {
			setTimeout(() => {
				setConfirmAddition(false);
			}, 4000);
		}
	}, [hasBeenAdded]);

	const handleAddToCart = () => {
		addItemToCart({ ...giftCard, quantity: quantity > 1 ? quantity : null });
		generateSubtotal();
		setHasBeenAdded(true);
		setConfirmAddition(true);
	};

	const handleQuantitySelect = () => {
		const menuItems = [];
		const limit = determineLimit(subtotal, siteId, userBalance?.totalLeft);

		for (let index = 1; index <= limit; index++) {
			menuItems.push(
				<MenuItem key={index} value={index}>
					{index}
				</MenuItem>
			);
		}
		return menuItems;
	};

	const isDisabled =
		isCartPastLimit(subtotal, siteId, userBalance?.totalLeft) ||
		userBalance?.nextAvailablePurchaseDate;

	const gcQty = `gcid-${giftCard.id}`;
	return (
		<Container
			sx={{
				my: { xs: '20px', sm: '30px', md: '40px' },
				'& img': { width: '100%', objectFit: 'contain', height: { xs: '155px', sm: '180px' } }
			}}
			maxWidth={false}
		>
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<IconButton
					disableFocusRipple
					disableRipple
					role='link'
					onClick={() => {
						window.scrollTo({ top: 0, behavior: 'smooth' });
						navigate(`/giftCard/${giftCard?.id}`);
					}}
					sx={{
						borderRadius: '10px',
						padding: '3px',
						mb: '15px',
						'&:hover': {
							opacity: 0.9
						},
						'&:focus': {
							outline: '2px dotted #002856'
						}
					}}
				>
					<img src={giftCard.image} alt={`$${giftCard.amount} ${giftCard.company} Gift Card`} />
				</IconButton>
			</Box>
			<Box sx={{ '& p': { m: 0, p: 0 } }}>
				<Stack spacing={2}>
					<Stack
						direction={{ xs: 'row', sm: 'column', md: 'row' }}
						justifyContent='center'
						alignItems='center'
						spacing={{ xs: 3, sm: 1, md: 2, lg: 3, xl: 4 }}
					>
						<Typography variant='body2'>{sentenceCase(giftCard.category)}</Typography>
						<Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
							${giftCard.amount} {giftCard.company} Gift Card
						</Typography>
					</Stack>
					<Stack
						direction='row'
						alignItems='center'
						justifyContent='center'
						spacing={{ xs: 4, sm: 2, md: 3, xl: 4 }}
					>
						<Typography variant='h6'>${giftCard.price}</Typography>
						<FormControl>
							<InputLabel htmlFor={gcQty}>Qty</InputLabel>
							<Select
								labelId={gcQty}
								id={gcQty}
								disabled={isDisabled}
								value={quantity}
								inputProps={{
									id: gcQty,
									'aria-label': 'Select quantity of Gift Card'
								}}
								size='small'
								label='Qty'
								onChange={(e) => setQuantity(e.target.value)}
							>
								{handleQuantitySelect()}
							</Select>
						</FormControl>
						<LoadingButton
							loading={hasBeenAdded}
							disabled={isDisabled}
							onClick={handleAddToCart}
							className='filter footerfilter'
							variant='contained'
							size='small'
							sx={{
								borderRadius: (theme) => theme.pixels.bRadius.main,
								padding: '9px 20px',
								boxShadow: 'none'
							}}
							aria-label={`Add $${giftCard.amount} ${giftCard.company} Gift Card to the Cart`}
						>
							<Typography
								variant='body2'
								sx={{
									textTransform: 'none',
									fontWeight: 600
								}}
							>
								{!hasBeenAdded && confirmAddition ? 'Added to Cart' : 'Add to Cart'}
							</Typography>
						</LoadingButton>
					</Stack>
				</Stack>
			</Box>
		</Container>
	);
};

GiftCardContainer.propTypes = {
	addItemToCart: PropTypes.func,
	generateSubtotal: PropTypes.func,
	giftCard: PropTypes.object,
	siteId: PropTypes.number,
	subtotal: PropTypes.number,
	userBalance: PropTypes.object
};

export default GiftCardContainer;
